import React from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaUsers, FaTrophy, FaGlobe, FaHandshake, FaRocket, FaInstagram, FaTwitter, FaFacebook } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';

// Import assets
import gymLifestyle1 from '../assets/images/gym-lifestyle-1.jpg';
import gymLifestyle2 from '../assets/images/gym-lifestyle-2.jpg';
import personalTrainer1 from '../assets/images/personal-trainer-1.jpg';
import personalTrainer2 from '../assets/images/personal-trainer-2.jpg';
import personalTrainer3 from '../assets/images/personal-trainer-3.jpg';
import personalTrainer4 from '../assets/images/personal-trainer-4.jpg';
import communityImg1 from '../assets/images/community-training-1.jpg';
import communityImg2 from '../assets/images/community-training-2.jpg';
import communityImg3 from '../assets/images/community-training-3.jpg';
import communityImg4 from '../assets/images/community-training-4.jpg';
import motivationAthlete from '../assets/images/motivation-athlete.jpg';
import communityVideo1 from '../assets/videos/community-workout-1.mp4';

const AboutPage = () => {
  const [ref1, inView1] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref3, inView3] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref4, inView4] = useInView({ threshold: 0.1, triggerOnce: true });

  const values = [
    {
      icon: <FaHeart size={40} />,
      title: 'Passion for Fitness',
      description: 'We live and breathe fitness. Our team is dedicated to helping you achieve your health and wellness goals.',
    },
    {
      icon: <FaUsers size={40} />,
      title: 'Community First',
      description: 'Building a supportive, inclusive community where everyone feels welcome and motivated to succeed.',
    },
    {
      icon: <FaTrophy size={40} />,
      title: 'Excellence',
      description: 'We partner with only the best certified coaches and fitness professionals to deliver world-class training.',
    },
    {
      icon: <FaGlobe size={40} />,
      title: 'Global Impact',
      description: 'Transforming lives across the globe, one workout at a time. Join our worldwide fitness movement.',
    },
    {
      icon: <FaHandshake size={40} />,
      title: 'Integrity',
      description: 'Honest, transparent, and committed to your success. Your trust is our most valuable asset.',
    },
    {
      icon: <FaRocket size={40} />,
      title: 'Innovation',
      description: 'Leveraging cutting-edge technology to create the most effective and engaging fitness experience.',
    },
  ];

  const team = [
    {
      name: 'Marcus Johnson',
      role: 'Head Coach & Founder',
      image: personalTrainer1,
      specialty: 'Strength & Conditioning',
    },
    {
      name: 'Sarah Williams',
      role: 'Nutrition Director',
      image: personalTrainer2,
      specialty: 'Sports Nutrition',
    },
    {
      name: 'David Chen',
      role: 'Lead Trainer',
      image: personalTrainer3,
      specialty: 'HIIT & Cardio',
    },
    {
      name: 'Emily Rodriguez',
      role: 'Wellness Coach',
      image: personalTrainer4,
      specialty: 'Yoga & Mindfulness',
    },
  ];

  const stats = [
    { number: '500K+', label: 'Active Members' },
    { number: '1000+', label: 'Expert Coaches' },
    { number: '150+', label: 'Countries' },
    { number: '10M+', label: 'Workouts Completed' },
  ];

  const communityFeatures = [
    {
      title: 'Group Challenges',
      description: 'Join monthly fitness challenges and compete with members worldwide.',
      image: communityImg1,
    },
    {
      title: 'Social Feed',
      description: 'Share your progress, celebrate wins, and motivate others in the community.',
      image: communityImg2,
    },
    {
      title: 'Live Classes',
      description: 'Participate in live workout sessions with coaches and fellow members.',
      image: communityImg3,
    },
    {
      title: 'Local Meetups',
      description: 'Connect with members in your area for group workouts and events.',
      image: communityImg4,
    },
  ];

  return (
    <div className="bg-black">
      {/* HERO SECTION */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={gymLifestyle1}
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>

        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-black mb-6">
                About <span className="gradient-text">MyFitness</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
                Empowering millions to transform their lives through fitness, community, and expert guidance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section ref={ref1} className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView1 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                Our <span className="gradient-text">Mission</span>
              </h2>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                At MyFitness, we believe that everyone deserves access to world-class fitness coaching and a supportive community. Our mission is to break down barriers and make professional fitness guidance accessible to all.
              </p>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                We've built a platform that connects you with certified coaches, provides personalized workout plans, and surrounds you with a community of like-minded individuals all working towards their fitness goals.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Whether you're just starting your fitness journey or you're a seasoned athlete, MyFitness is your partner in achieving greatness.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView1 ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={gymLifestyle2}
                  alt="Fitness Journey"
                  className="rounded-2xl w-full h-64 object-cover"
                />
                <img
                  src={motivationAthlete}
                  alt="Motivation"
                  className="rounded-2xl w-full h-64 object-cover mt-8"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 glass p-6 rounded-2xl max-w-xs">
                <p className="text-4xl font-black text-primary mb-2">500K+</p>
                <p className="text-gray-300">Lives Transformed</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section ref={ref2} className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Our <span className="gradient-text">Values</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              The principles that guide everything we do at MyFitness.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="glass rounded-2xl p-8 text-center"
              >
                <div className="inline-block p-4 bg-primary/10 rounded-full mb-6 text-primary">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                <p className="text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section ref={ref3} className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView3 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Meet Our <span className="gradient-text">Expert Team</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              World-class coaches and fitness professionals dedicated to your success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView3 ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl mb-4">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-primary font-semibold">{member.specialty}</p>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                <p className="text-gray-400">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="py-24 bg-gradient-to-r from-primary to-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-black text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-lg text-white/90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY SECTION */}
      <section ref={ref4} className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView4 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Join Our <span className="gradient-text">Community</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              More than just an app - it's a global movement of fitness enthusiasts supporting each other.
            </p>
          </motion.div>

          {/* Video Feature */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView4 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-16 rounded-2xl overflow-hidden"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[500px] object-cover"
            >
              <source src={communityVideo1} type="video/mp4" />
            </video>
          </motion.div>

          {/* Community Features */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {communityFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView4 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="glass rounded-2xl overflow-hidden group cursor-pointer"
              >
                <div className="relative h-64">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-gray-300">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView4 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="glass rounded-2xl p-12 text-center"
          >
            <h3 className="text-3xl font-bold mb-6">Connect With Us</h3>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Follow our journey on social media and join the conversation with thousands of fitness enthusiasts worldwide.
            </p>
            <div className="flex justify-center gap-6">
              {[
                { icon: <FaInstagram size={32} />, label: 'Instagram' },
                { icon: <FaTwitter size={32} />, label: 'Twitter' },
                { icon: <FaFacebook size={32} />, label: 'Facebook' },
              ].map((social, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.1, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-16 h-16 bg-primary hover:bg-primary/80 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  {social.icon}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Ready to Join the Movement?
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Become part of the MyFitness family and start your transformation today.
            </p>
            <button className="btn-primary text-lg">
              Get Started Now
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
